import { proveNever } from "@glideapps/ts-necessities";
import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { HeroImageEffect, HeroStyle } from "common/wire/types";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import tw from "twin.macro";
import { styled } from "webapp/styled-components";
import Img from "webapp/ui/smart/img/img";

// gp-sm:(max-width[400px] px-6) // content-area: 352
// gp-md:(max-width[620px] px-7) // content-area: 564
// gp-lg:(max-width[748px] px-8) // content-area: 684
// gp-xl:(max-width[1004px] px-8) // content-area: 940
// gp-2xl:(max-width[1516px] px-8) // content-area 1216

export enum SectionStyle {
    None = "none",
    Dark = "dark",
    White = "white",
    Accent = "accent",
    Image = "image",
}

export function heroStyleToSectionStyle(heroStyle: HeroStyle): SectionStyle {
    switch (heroStyle) {
        case HeroStyle.Accent:
            return SectionStyle.Accent;
        case HeroStyle.Dark:
            return SectionStyle.Dark;
        case HeroStyle.Image:
            return SectionStyle.Image;
        case HeroStyle.None:
        case HeroStyle.Card:
            return SectionStyle.None;
        case HeroStyle.White:
            return SectionStyle.White;
        default:
            return proveNever(heroStyle, "Unknown Hero style", SectionStyle.None);
    }
}

interface Props {
    background?: SectionStyle;
    backgroundImage?: string;
    backgroundEffect?: HeroImageEffect;
    blur?: boolean;
    className?: string;
}

const WireContainerStyle = styled.div`
    &.style-dark,
    &.style-image.darken {
        background-color: ${p => p.theme.n700};
    }

    &.style-image {
        background-color: ${p => p.theme.n200};
    }

    &.style-white,
    &.style-image.lighten {
        background-color: ${p => p.theme.n0};
    }
    
    &.darken .background-image,
    &.lighten .background-image {
        opacity: 0.5;
    }

    &.blur {
        overflow: hidden;
    }

    &:first-child.full-bleed,
    .full-bleed + &.full-bleed {
        margin-top: 0;
    }

    &.full-bleed:last-child {
        padding-bottom: 0;
    }
`;

export const WireContainer: GlideFC<React.PropsWithChildren<Props>> = p => {
    const {
        children,
        background = SectionStyle.None,
        backgroundImage,
        backgroundEffect = HeroImageEffect.None,
        blur = false,
        className,
    } = p;

    return (
        <SectionStyleProvider value={background}>
            <WireContainerStyle
                className={classNames(
                    "section-wrap",
                    background !== SectionStyle.None && "full-bleed",
                    background === SectionStyle.Accent && "accent-bg",
                    background === SectionStyle.Image && blur && "blur",
                    backgroundEffect.toLocaleLowerCase(),
                    `style-${background.toLowerCase()}`,
                    className
                )}
                tw="isolate relative w-full flex justify-center mt-6 last:pb-6">
                {background === SectionStyle.Image && backgroundImage !== undefined && (
                    <Img
                        className="background-image"
                        css={[
                            tw`absolute inset-0 object-cover w-full h-full pointer-events-none filter`,
                            blur && tw`transform scale-125 blur-md`,
                        ]}
                        data-test="hero-background-image"
                        src={massageImageUrl(
                            backgroundImage,
                            {
                                width: window.innerWidth,
                                thumbnail: false,
                            },
                            undefined
                        )}
                        width={"100%"}
                        height={400}
                        draggable={false}
                    />
                )}
                <div
                    className="section-container"
                    tw="z-10 w-full relative flex flex-col px-5
                        gp-sm:(px-6)
                        gp-md:(px-7)
                        gp-lg:(px-8)
                        gp-2xl:(max-width[1260px])">
                    {children}
                </div>
            </WireContainerStyle>
        </SectionStyleProvider>
    );
};

export const SectionStyleContext = React.createContext<SectionStyle>(SectionStyle.None);

export function useSectionStyle() {
    return React.useContext(SectionStyleContext);
}

export const SectionStyleProvider: React.FC<{ value: SectionStyle }> = p => {
    let { value } = p;
    const parent = useSectionStyle();
    if (value === SectionStyle.None) {
        value = parent;
    }
    return <SectionStyleContext.Provider value={value}>{p.children}</SectionStyleContext.Provider>;
};
